// Story 7.6 — polymorphic feed card dispatcher, keyed on source_kind.
//
// Pinned chip renders ABOVE the frame; is_promo gets NO visual
// differentiation whatsoever per Sally's anti-salesmanship rule ("stop
// flagging promos as promos" — the card is just the card). Unknown
// source kinds degrade to the editorial shape rather than crashing —
// forward-compat for kinds added server-side before the SPA learns them.
//
// Component review checklist (AC 9): verify no SoMe affordances added —
// no like/heart, no comment count, no follow, no share/repost, no quote.
// Permitted interactions per card: link-out CTA, creator mark → public
// profile, embedded player controls. Nothing else.

import { BulletinCard } from "./BulletinCard";
import { CreatorPostCard } from "./CreatorPostCard";
import { CuratedNewsCard } from "./CuratedNewsCard";
import { DossierCard } from "./DossierCard";
import { EditorialCard } from "./EditorialCard";
import { KickClipCard } from "./KickClipCard";
import { PinnedChip } from "./GreenholdFrame";
import { SilenceCard } from "./SilenceCard";
import { TwitchClipCard } from "./TwitchClipCard";
import { YoutubeShortCard } from "./YoutubeShortCard";
import type { FeedCard as FeedCardData } from "./useFeed";

function CardBody({ card }: { card: FeedCardData }) {
  switch (card.source_kind) {
    case "creator_post":
      return <CreatorPostCard card={card} />;
    case "twitch_clip":
    case "twitch_popular":
      return <TwitchClipCard card={card} />;
    case "youtube_short":
    case "youtube_popular":
      return <YoutubeShortCard card={card} />;
    case "kick_clip":
      return <KickClipCard card={card} />;
    case "hltv_rss":
    case "lolesports_json":
      return <CuratedNewsCard card={card} />;
    case "system_silence":
      return <SilenceCard card={card} />;
    case "bulletin":
      return <BulletinCard card={card} />;
    case "platform_dossier":
      return <DossierCard card={card} />;
    case "admin_editorial":
    default:
      return <EditorialCard card={card} />;
  }
}

export function FeedCard({ card }: { card: FeedCardData }) {
  return (
    <div data-testid="feed-card">
      {card.is_pinned ? <PinnedChip /> : null}
      <CardBody card={card} />
    </div>
  );
}
